import React, { FC } from 'react'
import Link from 'components/Link'

// Constants
import { routes } from 'constants/routes'

// Components
import ConfirmationDialog from 'components/ConfirmationDialog'

// Containers
import { ModalInjectedProps } from 'containers/ModalManager'
import { useToast } from 'containers/ToastManager'

// Utils
import { deleteEntityFromCache } from 'utils'
import useGetHasDraftsProject from 'modules/projects/operations/useGetHasDraftsProject'
import { useRemoveWorkspaceMember } from 'modules/workspace/operations'

interface LeaveWorkspaceModalProps extends ModalInjectedProps {
  workspace: { identifier: string; name: string }
  membershipId: string
}

/**
 * LeaveWorkspaceModal
 *
 * This component is visually a Modal that Prompts the user
 * with a confirmation to leave the workspace
 */
const LeaveWorkspaceModal: FC<LeaveWorkspaceModalProps> = props => {
  const { hideModal, isModalOpen, workspace, membershipId } = props

  const { showToast } = useToast()

  const [removeMember, { loading }] = useRemoveWorkspaceMember({
    variables: { membershipId },
    onError: message => showToast(message, 'negative'),
    onCompleted: () => {
      showToast(`You have been removed from "${workspace.name}"`)
      hideModal()
    },
    update: cache => {
      /* Remove all the workspace information from the cache */
      deleteEntityFromCache(cache, {
        __typename: 'Workspace',
        identifier: workspace.identifier,
      })
    },
  })

  const hasDraftsProject = useGetHasDraftsProject(workspace.identifier)

  return (
    <ConfirmationDialog
      title={`Leave the "${workspace.name}" Workspace?`}
      hideModal={hideModal}
      isModalOpen={isModalOpen}
      onConfirm={removeMember}
      confirmButton={{
        text: 'Leave Workspace',
        loading,
        disabled: loading,
      }}>
      <p>
        After you leave you won&apos;t be able to view, comment or upload
        documents to the &quot;{workspace.name}&quot; Workspace anymore.
      </p>
      {hasDraftsProject && (
        <p>
          Make sure you back up any private work in
          <Link
            to={routes.WORKSPACE_DRAFTS.create({
              workspaceId: workspace.identifier,
            })}
            onClick={() => hideModal()}>
            {' '}
            My Drafts{' '}
          </Link>
          before you leave.
        </p>
      )}
    </ConfirmationDialog>
  )
}

export default LeaveWorkspaceModal
